import { useCallback, useEffect, useMemo, useState } from 'react';
import type { MulticamAngle } from '../../graphqlService';
import {
  SpeakerLookup,
  SpeakingActivity,
  createSpeakerLookup,
  currentActivity,
  speakerNameOf,
} from '../../utils/speakerActivity';

export interface SpeakersState {
  /** Who speaks when, for the angles as they are; null until worked out. */
  activity: SpeakingActivity | null;
  setActivity: (activity: SpeakingActivity | null) => void;
  /** Each angle's person as transcripts name them. */
  names: Record<string, string>;
  /** The names the creator typed ('' where they didn't). */
  ownNames: Record<string, string>;
  setOwnNames: (names: Record<string, string>) => void;
  /** For the transcript; undefined when PithPlay can't name anyone. */
  speakerAt?: SpeakerLookup;
}

/**
 * The set's speaking activity and its people's names, for the multicam
 * studio's transcript and suggested cuts: as loaded with the set, then as
 * worked out or renamed here.
 */
export function useSpeakingActivity(loaded: SpeakingActivity | null | undefined, angles: MulticamAngle[]): SpeakersState {
  const [stored, setStored] = useState<SpeakingActivity | null>(loaded ?? null);
  const [renamed, setRenamed] = useState<Record<string, string>>({});

  // The set (re)loaded.
  useEffect(() => {
    setStored(loaded ?? null);
  }, [loaded]);

  const ownNames = useMemo(() => {
    const out: Record<string, string> = {};
    angles.forEach((angle) => {
      out[angle.angleKey] = renamed[angle.angleKey] ?? String(angle.speakerName ?? '');
    });
    return out;
  }, [angles, renamed]);

  const named = useMemo(() => angles.map((angle) => ({ ...angle, speakerName: ownNames[angle.angleKey] })), [angles, ownNames]);
  const names = useMemo(() => {
    const out: Record<string, string> = {};
    named.forEach((angle) => {
      out[angle.angleKey] = speakerNameOf(angle);
    });
    return out;
  }, [named]);

  const activity = useMemo(() => currentActivity(stored, angles), [stored, angles]);
  const speakerAt = useMemo(() => createSpeakerLookup(activity, named), [activity, named]);

  const setActivity = useCallback((next: SpeakingActivity | null) => setStored(next), []);
  const setOwnNames = useCallback((next: Record<string, string>) => {
    setRenamed((previous) => ({ ...previous, ...next }));
  }, []);

  return { activity, setActivity, names, ownNames, setOwnNames, speakerAt };
}
